import runtimeEnv from "@mars/heroku-js-runtime-env";
import Grid from "@material-ui/core/Grid";
import { withStyles } from "@material-ui/core/styles";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Elements, StripeProvider } from "react-stripe-elements";
import useLocalStorageState from "use-local-storage-state";
import { v4 as uuidv4 } from "uuid";
import Boxes from "./components/Boxes";
import Confirm from "./components/Confirm";
import Confirmed from "./components/Confirmed";
import Introduction from "./components/Introduction";
import Locations from "./components/Locations";
import MovingDate from "./components/MovingDate";
import OriginDestination from "./components/OriginDestination";
import Quote from "./components/Quote";
import ResponsiveStepper from "./components/ResponsiveStepper";
import Schedule from "./components/Schedule";
import { renameSqftForBackend } from "./helpers";

const styles = (theme) => ({
  stepContentWrapper: {
    marginBottom: theme.spacing(4),
  },
});

const App = (props) => {
  window.dataLayer = window.dataLayer || [];

  const [IntroductionFormValues, setIntroductionFormValues] =
    useLocalStorageState("IntroductionFormValues", { defaultValue: {} });
  const [MovingDateFormValues, setMovingDateFormValues] = useLocalStorageState(
    "MovingDateFormValues",
    { defaultValue: {} }
  );
  const [OriginDestinationFormValues, setOriginDestinationFormValues] =
    useLocalStorageState("OriginDestinationFormValues", { defaultValue: {} });
  const [LocationsFormValues, setLocationsFormValues] = useLocalStorageState(
    "LocationsFormValues",
    { defaultValue: {} }
  );
  const [QuoteFormValues, setQuoteFormValues] = useLocalStorageState(
    "QuoteFormValues",
    { defaultValue: {} }
  );
  const [quote, setQuote] = useLocalStorageState("quote", { defaultValue: {} });
  const [BoxesFormValues, setBoxesFormValues] = useLocalStorageState(
    "BoxesFormValues",
    { defaultValue: {} }
  );
  const [boxes, setBoxes] = useLocalStorageState("boxes", { defaultValue: [] });
  const [ScheduleFormValues, setScheduleFormValues] = useLocalStorageState(
    "ScheduleFormValues",
    { defaultValue: [] }
  );
  const [ConfirmFormValues, setConfirmFormValues] = useLocalStorageState(
    "ConfirmFormValues",
    { defaultValue: {} }
  );
  const [confirmedQuote, setConfirmedQuote] = useLocalStorageState(
    "confirmedQuote",
    { defaultValue: {} }
  );
  const [activeStep, setActiveStep] = useLocalStorageState("activeStep", {
    defaultValue: 0,
  });
  const [uuid, setUuid] = useLocalStorageState("uuid");
  const [name, setName] = useLocalStorageState("name");
  const [email, setEmail] = useLocalStorageState("email");
  const [phone, setPhone] = useLocalStorageState("phone");
  const [estimateValues, setEstimateValues] = useLocalStorageState(
    "estimateValues",
    { defaultValue: {} }
  );

  const formValues = {
    IntroductionFormValues: setIntroductionFormValues,
    MovingDateFormValues: setMovingDateFormValues,
    OriginDestinationFormValues: setOriginDestinationFormValues,
    LocationsFormValues: setLocationsFormValues,
    QuoteFormValues: setQuoteFormValues,
    BoxesFormValues: setBoxesFormValues,
    ScheduleFormValues: setScheduleFormValues,
    ConfirmFormValues: setConfirmFormValues,
  };

  useEffect(() => {
    if (!uuid) {
      setUuid(uuidv4());
    }
    var url = new URL(window.location.href);
    var quoteId = url.searchParams.get("quote");
    if (quoteId) {
      axios
        .request({
          method: "get",
          url: runtimeEnv().REACT_APP_API_URL + "/quote",
          params: {
            id: quoteId,
          },
        })
        .then((response) => {
          const {
            name,
            email,
            phone,
            locationsFormValues: quoteLocationsFormValues,
            movingDateFormValues: quoteMovingDateFormValues,
            originDestinationFormValues: quoteOriginDestinationFormValues,
            quote,
          } = response.data;
          setUuid(quoteId);

          const parsedLocationsFormValues = JSON.parse(
            quoteLocationsFormValues
          );
          const parsedMovingDateFormValues = JSON.parse(
            quoteMovingDateFormValues
          );
          const parsedOriginDestinationFormValues = JSON.parse(
            quoteOriginDestinationFormValues
          );

          setLocationsFormValues(parsedLocationsFormValues);
          setMovingDateFormValues(parsedMovingDateFormValues);
          setOriginDestinationFormValues(parsedOriginDestinationFormValues);
          setQuote(JSON.parse(quote));
          setName(name);
          setEmail(email);
          setPhone(phone);

          let newEstimateValues = {
            ...parsedLocationsFormValues,
            originLocationId:
              parsedOriginDestinationFormValues.originLocationId.value,
            destinationLocationId:
              parsedOriginDestinationFormValues.destinationLocationId.value,
            movingDateTime: parsedOriginDestinationFormValues.date,
            uuid: quoteId,
            name: name,
            email: email,
            phone: phone,
            locationsFormValues: JSON.stringify(parsedLocationsFormValues),
            movingDateFormValues: JSON.stringify(parsedMovingDateFormValues),
            originDestinationFormValues: JSON.stringify(
              parsedOriginDestinationFormValues
            ),
            quote: JSON.parse(quote),
          };
          newEstimateValues = renameSqftForBackend(newEstimateValues);
          setEstimateValues(newEstimateValues);

          setActiveStep(4);
          if (window.location.host.includes("gomovingbuddy.com")) {
            window.history.pushState({}, "", "/book-a-move");
          } else {
            window.history.pushState({}, "", "/");
          }
        })
        .catch((error) => {
          if (window.location.host.includes("gomovingbuddy.com")) {
            window.history.pushState({}, "", "/book-a-move");
          } else {
            window.history.pushState({}, "", "/");
          }
          console.log(error); //eslint-disable-line no-console
          console.log(error.response); //eslint-disable-line no-console
        });
    }
  }, []);

  // For debugging:
  // const today = new Date();
  // const tomorrow = new Date(today);
  // tomorrow.setDate(tomorrow.getDate() + 5);
  // state = {
  //   MovingDateFormValues: { date: tomorrow }, //Capitalized to match the step names
  //   OriginDestinationFormValues: {
  //     originLocationId: {
  //       value:
  //         "EipDbGV2ZWxhbmQgU3RyZWV0LCBCcnVuc3dpY2ssIE9IIDQ0MjEyLCBVU0EiLiosChQKEgnpqK48S8cwiBHLrvaei1HoNxIUChIJk_CH8cfAMIgRZirx4y0FyrE",
  //       label: "Cleveland Street, Brunswick, OH 44212, USA",
  //       mainText: "Cleveland Street",
  //       secondaryText: "Brunswick, OH 44212, USA",
  //     },
  //     destinationLocationId: {
  //       value:
  //         "EipDbGV2ZWxhbmQgU3RyZWV0LCBCcnVuc3dpY2ssIE9IIDQ0MjEyLCBVU0EiLiosChQKEgnpqK48S8cwiBHLrvaei1HoNxIUChIJk_CH8cfAMIgRZirx4y0FyrE",
  //       label: "Cleveland Street, Brunswick, OH 44212, USA",
  //       mainText: "Cleveland Street",
  //       secondaryText: "Brunswick, OH 44212, USA",
  //     },
  //   },
  //   LocationsFormValues: {
  //     originTypeOf: "house",
  //     originUnitNumber: "",
  //     originSqFt: "3b",
  //     originSqFtApt: "",
  //     originSqFtMiniStorage: "",
  //     originFloor: "",
  //     originNumFloors: 3,
  //     originElevator: "",
  //     destinationTypeOf: "apt",
  //     destinationUnitNumber: "1",
  //     destinationSqFt: "",
  //     destinationSqFtApt: "1b",
  //     destinationSqFtMiniStorage: "",
  //     destinationFloor: 12,
  //     destinationNumFloors: "",
  //     destinationElevator: "1",
  //     amountOfStuff: "3",
  //     itemsIn: "both",
  //   },
  //   QuoteFormValues: {},
  //   quote: {
  //     originId:
  //       "EipDbGV2ZWxhbmQgU3RyZWV0LCBCcnVuc3dpY2ssIE9IIDQ0MjEyLCBVU0EiLiosChQKEgnpqK48S8cwiBHLrvaei1HoNxIUChIJk_CH8cfAMIgRZirx4y0FyrE",
  //     originTypeOf: "house",
  //     destinationTypeOf: "apt",
  //     originSqFt: "3b",
  //     destinationSqFt: "1b",
  //     originFloor: null,
  //     destinationFloor: 12,
  //     originNumFloors: 3,
  //     destinationNumFloors: null,
  //     originElevator: false,
  //     destinationElevator: true,
  //     destinationId:
  //       "EipDbGV2ZWxhbmQgU3RyZWV0LCBCcnVuc3dpY2ssIE9IIDQ0MjEyLCBVU0EiLiosChQKEgnpqK48S8cwiBHLrvaei1HoNxIUChIJk_CH8cfAMIgRZirx4y0FyrE",
  //     amountOfStuff: 3,
  //     itemsIn: "both",
  //     originUnitNumber: "estimate",
  //     destinationUnitNumber: "estimate",
  //     amount_off: 0,
  //     percent_off: 0,
  //   },
  //   BoxesFormValues: {}, //Capitalized to match the step names
  //   boxes: [],
  //   ScheduleFormValues: [], //Capitalized to match the step names
  //   ConfirmFormValues: {}, //Capitalized to match the step names
  //   confirmedQuote: {},
  //   activeStep: 3,
  // };
  // }

  const steps = [
    {
      title: "General Info",
      stepNumber: 0,
      componentName: "Introduction",
      displayInStepper: true,
      isOptional: false,
    },
    {
      title: "General Info",
      stepNumber: 0,
      componentName: "MovingDate",
      displayInStepper: false,
      isOptional: false,
    },
    {
      title: "General Info",
      stepNumber: 0,
      componentName: "OriginDestination",
      displayInStepper: false,
      isOptional: false,
    },
    {
      title: "Moving From & To",
      stepNumber: 1,
      componentName: "Locations",
      displayInStepper: true,
      isOptional: false,
    },
    {
      title: "Quote",
      stepNumber: 2,
      componentName: "Quote",
      displayInStepper: true,
      isOptional: false,
    },
    {
      title: "Boxes",
      stepNumber: 3,
      componentName: "Boxes",
      displayInStepper: true,
      isOptional: true,
    },
    {
      title: "Schedule",
      stepNumber: 4,
      componentName: "Schedule",
      displayInStepper: true,
      isOptional: false,
    },
    {
      title: "Confirm",
      stepNumber: 5,
      componentName: "Confirm",
      displayInStepper: true,
      isOptional: false,
    },
    {
      title: "Confirmed",
      stepNumber: 6,
      componentName: "Confirmed",
      displayInStepper: false,
      isOptional: false,
    },
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Introduction
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            handleNext={handleNext}
          />
        );
      case 1:
        return (
          <MovingDate
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            values={MovingDateFormValues}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 2:
        return (
          <OriginDestination
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            values={OriginDestinationFormValues}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 3:
        return (
          <Locations
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            values={LocationsFormValues}
            originDestinationFormValues={OriginDestinationFormValues}
            movingDateFormValues={MovingDateFormValues}
            updateQuote={setQuote}
            handleBack={handleBack}
            handleNext={handleNext}
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            phone={phone}
            setPhone={setPhone}
            uuid={uuid}
            MovingDateFormValues={MovingDateFormValues}
            OriginDestinationFormValues={OriginDestinationFormValues}
            setEstimateValues={setEstimateValues}
          />
        );
      case 4:
        return (
          <Quote
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            quote={quote}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 5:
        return (
          <Boxes
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            values={BoxesFormValues}
            updateBoxes={setBoxes}
            handleBack={handleBack}
            handleNext={handleNext}
          />
        );
      case 6:
        return (
          <Schedule
            steps={steps.length} //Need this to pass to the mobileStepper
            activeStep={activeStep} //Need this to pass to the mobileStepper
            values={ScheduleFormValues}
            movingDate={MovingDateFormValues.date}
            numberOfTrucks={quote.trucks}
            hours={quote.estimatedDuration}
            handleBack={handleBack}
            handleNext={handleNext}
            estimateValues={estimateValues}
            updateQuote={setQuote}
          />
        );
      case 7:
        return (
          <StripeProvider
            apiKey={runtimeEnv().REACT_APP_STRIPE_PUBLISHABLE_KEY}
          >
            <Elements>
              <Confirm
                values={ConfirmFormValues}
                quote={quote}
                setConfirmedQuote={setConfirmedQuote}
                updateQuote={setQuote}
                handleRemoveBoxes={handleRemoveBoxes}
                originDestinationFormValues={OriginDestinationFormValues}
                locationsFormValues={{ ...LocationsFormValues }}
                boxesFormValues={BoxesFormValues}
                boxes={boxes}
                scheduleFormValues={ScheduleFormValues}
                handleBack={handleBack}
                handleNext={handleNext}
                uuid={uuid}
                steps={steps.length}
                activeStep={activeStep}
              />
            </Elements>
          </StripeProvider>
        );
      case 8:
        return (
          <Confirmed
            values={confirmedQuote}
            quote={quote}
            originDestinationFormValues={OriginDestinationFormValues}
            locationsFormValues={{ ...LocationsFormValues }}
            boxesFormValues={BoxesFormValues}
            boxes={boxes}
            scheduleFormValues={ScheduleFormValues}
          />
        );
      default:
        return "Unknown step";
    }
  };

  const handleNext = (values, skipNextStep) => {
    //Store values from this step in the state in case we go back a step
    //Then go to the next step
    // set
    const prevActiveStep = activeStep;
    formValues[steps[prevActiveStep].componentName + "FormValues"](
      values || {}
    );
    setActiveStep(skipNextStep ? prevActiveStep + 2 : prevActiveStep + 1);
    window.scrollTo(0, 0);
    // setState(
    //   (prevState) => ({
    //     [steps[prevState.activeStep].componentName + "FormValues"]: values,
    //     activeStep: skipNextStep
    //       ? prevState.activeStep + 2
    //       : prevState.activeStep + 1,
    //   }),
    //   () => window.scrollTo(0, 0)
    // );
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleRemoveBoxes = () => {
    setBoxesFormValues({});
  };

  const { classes } = props;

  return (
    <Grid container justify="center" spacing={4}>
      <Grid item xs={12}>
        <ResponsiveStepper steps={steps} activeStep={activeStep} />
      </Grid>
      <Grid item className={classes.stepContentWrapper}>
        {getStepContent(activeStep)}
      </Grid>
    </Grid>
  );
  // }
};

App.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(App);
